import { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import Icon from "../../../components/Icon";
import MessageBubble from "./MessageBubble";
import dayjs from "dayjs";

function dayLabel(s) {
  const d = dayjs(s);
  const today = dayjs().startOf("day");
  if (d.isSame(today, "day")) return "Today";
  if (d.isSame(today.subtract(1, "day"), "day")) return "Yesterday";
  return d.format("DD MMM YYYY");
}

// Same sender within 4 minutes → collapse into one cluster (hide repeat avatar).
function leadsCluster(msg, prev) {
  if (!prev) return true;
  if (prev.sender?.id !== msg.sender?.id) return true;
  return dayjs(msg.created_at).diff(dayjs(prev.created_at), "minute") >= 4;
}

const MessageList = forwardRef(function MessageList(
  { messages, currentUserId, isDM, readMarker, hasMore, loadingOlder, onLoadOlder, unreadBeforeId, ...handlers },
  ref,
) {
  const scrollRef = useRef(null);
  const prevFirstId = useRef(null);
  const prevHeight = useRef(0);
  const nearBottom = useRef(true);
  const [showJump, setShowJump] = useState(false);

  const scrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    nearBottom.current = true;
    setShowJump(false);
  };

  useImperativeHandle(ref, () => ({ scrollToBottom, isNearBottom: () => nearBottom.current }));

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const firstId = messages[0]?.id ?? null;
    const olderPrepended =
      prevFirstId.current && firstId && firstId !== prevFirstId.current &&
      messages.some((m) => m.id === prevFirstId.current);

    if (olderPrepended) {
      el.scrollTop = el.scrollHeight - prevHeight.current; // hold position
    } else if (nearBottom.current) {
      el.scrollTop = el.scrollHeight;
      setShowJump(false);
    } else {
      setShowJump(true);
    }
    prevFirstId.current = firstId;
    prevHeight.current = el.scrollHeight;
  }, [messages]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    nearBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (nearBottom.current) setShowJump(false);
    if (el.scrollTop < 48 && hasMore && !loadingOlder) onLoadOlder();
  };

  let lastDay = null;

  return (
    <div className="chat-msglist-wrap">
      <div className="chat-msglist" ref={scrollRef} onScroll={onScroll}>
        {loadingOlder && <div className="chat-loading-older">Loading earlier messages…</div>}

        {messages.map((m, i) => {
          const prev = messages[i - 1];
          const thisDay = dayjs(m.created_at).format("YYYY-MM-DD");
          const dayChanged = thisDay !== lastDay;
          lastDay = thisDay;
          const lead = dayChanged || leadsCluster(m, prev);

          return (
            <div key={m.id}>
              {dayChanged && <div className="chat-day-sep"><span>{dayLabel(m.created_at)}</span></div>}
              {unreadBeforeId === m.id && <div className="chat-unread-sep"><span>New messages</span></div>}
              <MessageBubble
                message={m}
                isOwn={m.sender?.id === currentUserId}
                isDM={isDM}
                showAvatar={lead}
                readMarker={readMarker}
                {...handlers}
              />
            </div>
          );
        })}
      </div>

      {showJump && (
        <button type="button" className="chat-jump" onClick={scrollToBottom} title="Jump to latest">
          <Icon name="arrow-down" size={16} />
        </button>
      )}
    </div>
  );
});

export default MessageList;
